import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Sidebar from "../component/Sidebar";
import Topbar from "../component/Topbar";
import Footer from "../component/Footer";
import CommonTable from "../component/CommonTable";
import { exportToExcel } from "../utils/exportData";
import ExportButton from "../component/ExportButton";
import { BASE_URL } from "../api";
import {
    getApiHeaders,
    formatDate,
    formatDateDDMMYYYY,
    shortAddress,
    getExplorerAddressUrl,
    getExplorerTxUrl,
    getExplorerName
} from "../utils/helpers";
import axios from "axios";
import copy from "copy-to-clipboard";
import MessageToster from "../component/MessageToster";
import MobileFilter from "../component/MobileFilter";

export default function WithdrawHistory() {
    const [data, setData] = useState([]);
    const navigate = useNavigate();
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState("");
    const [copiedAddressId, setCopiedAddressId] = useState(null);
    const [copiedKey, setCopiedKey] = useState(null);
    const [selectedWithdraw, setSelectedWithdraw] = useState(null);

    const today = new Date().toISOString().split("T")[0];
    const [filters, setFilters] = useState({
        fromDate: "",
        toDate: ""
    });
    const [appliedFilters, setAppliedFilters] = useState({
        fromDate: "",
        toDate: "",
        search: ""
    });

    // Close modal on Escape key
    useEffect(() => {
        const handleKeyDown = (e) => {
            if (e.key === "Escape") {
                setSelectedWithdraw(null);
            }
        };
        if (selectedWithdraw) {
            window.addEventListener("keydown", handleKeyDown);
        }
        return () => window.removeEventListener("keydown", handleKeyDown);
    }, [selectedWithdraw]);

    const handleCopyText = (text, key, label = "Text") => {
        if (!text) return;
        copy(String(text).trim());
        setCopiedKey(key);
        setCopiedAddressId(key);
        MessageToster("success", `${label} copied to clipboard!`);
        setTimeout(() => {
            setCopiedKey(null);
            setCopiedAddressId(null);
        }, 2000);
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFilters(prev => ({ ...prev, [name]: value }));
    };

    const clearFilters = () => {
        setFilters({
            fromDate: "",
            toDate: ""
        });
        setSearchTerm("");
        setAppliedFilters({
            fromDate: "",
            toDate: "",
            search: ""
        });
    };

    const handleSearch = () => {
        setAppliedFilters({ ...filters, search: searchTerm });
    };

    useEffect(() => {
        const fetchHistory = async () => {
            try {
                const res = await axios.post(`${BASE_URL}/withdraw/withdraw_list`, {}, {
                    headers: getApiHeaders()
                }).catch(async () => {
                    return await axios.get(`${BASE_URL}/withdraw/withdraw_list`, {
                        headers: getApiHeaders()
                    });
                });

                const raw = res.data?.data ?? res.data?.withdraw_list ?? (Array.isArray(res.data) ? res.data : []);

                let list = [];
                if (Array.isArray(raw)) {
                    list = Array.isArray(raw[0]) ? raw.flat(Infinity) : raw;
                } else if (raw && typeof raw === "object") {
                    list = [raw];
                }

                // Filter out database status-only objects like [{ msg: "Done", msgcode: "Success" }]
                const validRecords = list.filter((item) => {
                    if (!item || typeof item !== "object") return false;
                    const keys = Object.keys(item);
                    const nonMsgKeys = keys.filter(k => !['msg', 'msgcode', 'Msg', 'MsgCode', 'message'].includes(k));
                    return nonMsgKeys.length > 0;
                });

                setData(validRecords);
            } catch (error) {
                console.error("Error fetching withdraw history:", error);
                setData([]);
            } finally {
                setLoading(false);
            }
        };

        fetchHistory();
    }, []);

    const getItemDateString = (dateVal) => {
        if (!dateVal) return "";
        if (typeof dateVal === "string") {
            if (dateVal.includes("T")) return dateVal.split("T")[0];
            if (dateVal.includes(" ")) return dateVal.split(" ")[0];
        }
        const d = new Date(dateVal);
        if (!isNaN(d.getTime())) {
            const year = d.getFullYear();
            const month = String(d.getMonth() + 1).padStart(2, "0");
            const day = String(d.getDate()).padStart(2, "0");
            return `${year}-${month}-${day}`;
        }
        return "";
    };

    const filteredData = data.filter(item => {
        // Search filter
        const search = (appliedFilters.search || "").trim().toLowerCase();
        if (search) {
            const formattedDate = formatDate(item.requested_at || item.ondate || item.created_at);
            const searchPool = [
                item.id,
                item.orderno,
                item.uloginid,
                item.amount,
                `$${item.amount}`,
                item.fee_amount,
                item.payable,
                `$${item.payable}`,
                item.token_symbol,
                item.coin_symbol,
                item.withdraw_type,
                item.chain_name,
                item.chain_type,
                item.withdrawal_address,
                item.tx_hash,
                item.status,
                item.istatus,
                formattedDate
            ];
            const matchesSearch = searchPool.some(val =>
                val != null && String(val).toLowerCase().includes(search)
            );
            if (!matchesSearch) return false;
        }

        // Date filter
        const itemDate = getItemDateString(item.requested_at || item.ondate || item.created_at);
        if (appliedFilters.fromDate && itemDate && itemDate < appliedFilters.fromDate) {
            return false;
        }
        if (appliedFilters.toDate && itemDate && itemDate > appliedFilters.toDate) {
            return false;
        }

        return true;
    });

    const handleExport = () => {
        const exportData = filteredData.map((item, index) => ({
            "#": index + 1,
            "Date": formatDateDDMMYYYY(item.requested_at || item.ondate || item.created_at) || "-",
            "Coin Symbol": item.token_symbol || item.coin_symbol || item.withdraw_type || "USDT",
            "Login ID": item.uloginid || "-",
            "Amount": item.amount != null ? Number(item.amount).toFixed(2) : 0,
            "Fee": item.fee_amount != null ? Number(item.fee_amount).toFixed(2) : 0,
            "Payable": item.payable != null ? Number(item.payable).toFixed(2) : 0,
            "Network": item.chain_name || item.chain_type || "-",
            "Address": item.withdrawal_address || "-",
            "Tx Hash": item.tx_hash || "-",
            "Status": item.status || item.istatus || "-"
        }));
        exportToExcel(exportData, "Crypto_Withdraw_History", "default");
    };

    const renderStatusBadge = (status) => {
        const rawStatus = status || "-";
        const statusStr = String(rawStatus).toLowerCase();
        const isPending = ['pending', '0', 'processing', 'in review'].includes(statusStr);
        const isSuccess = ['confirm', 'success', 'completed', 'approved', '1', 'done'].includes(statusStr);
        const isRejected = ['cancelled', 'canceled', 'rejected', 'failed', 'declined'].includes(statusStr);

        let bgClass = "bg-secondary bg-opacity-10 text-secondary";
        let icon = "bx-help-circle";

        if (isPending) {
            bgClass = "bg-warning bg-opacity-10 text-warning";
            icon = "bx-time-five";
        } else if (isSuccess) {
            bgClass = "bg-success bg-opacity-10 text-success";
            icon = "bx-check-circle";
        } else if (isRejected) {
            bgClass = "bg-danger bg-opacity-10 text-danger";
            icon = "bx-x-circle";
        }

        return (
            <span
                className={`badge fw-bold px-3 py-2 rounded-pill d-inline-flex align-items-center gap-1 ${bgClass}`}
            >
                <i className={`bx ${icon}`}></i>
                <span>{rawStatus}</span>
            </span>
        );
    };

    const columns = [
        {
            header: "Date",
            render: (row) => formatDateDDMMYYYY(row.requested_at || row.ondate || row.created_at) || "-",
            headerStyle: { width: "160px" }
        },
        {
            header: "Coin Symbol",
            render: (row) => (
                <span className="fw-semibold notranslate" translate="no">
                    {row.token_symbol || row.coin_symbol || row.withdraw_type || "USDT"}
                </span>
            )
        },
        {
            header: "Login ID",
            accessor: "uloginid",
            render: (row) => row.uloginid || "-"
        },
        {
            header: "Amount",
            render: (row) => (
                <span className="fw-bold text-danger">
                    {row.amount != null ? `$${Number(row.amount).toFixed(2)}` : "-"}
                </span>
            )
        },
        {
            header: "Fee",
            render: (row) => (
                <span className="text-muted">
                    {row.fee_amount != null ? `$${Number(row.fee_amount).toFixed(2)}` : "$0.00"}
                </span>
            )
        },
        {
            header: "Payable",
            render: (row) => (
                <span className="fw-bold text-success">
                    {row.payable != null ? `$${Number(row.payable).toFixed(2)}` : "-"}
                </span>
            )
        },
        {
            header: "Network",
            render: (row) => row.chain_name || row.chain_type || "-"
        },
        {
            header: "Address",
            render: (row, idx) => {
                if (!row.withdrawal_address) return "-";
                const isCopied = copiedAddressId === (row.id || idx) || copiedKey === `addr_tbl_${row.id || idx}`;
                const scanUrl = getExplorerAddressUrl(row.chain_name || row.chain_type, row.withdrawal_address);
                return (
                    <div className="d-flex align-items-center justify-content-end justify-content-md-start gap-2 flex-nowrap">
                        <span title={row.withdrawal_address} className="font-monospace text-nowrap">
                            {shortAddress(row.withdrawal_address) || row.withdrawal_address}
                        </span>
                        <button
                            type="button"
                            className="btn btn-sm btn-link text-decoration-none p-0 border-0"
                            onClick={() => handleCopyText(row.withdrawal_address, `addr_tbl_${row.id || idx}`, "Address")}
                            title="Copy address"
                            style={{ cursor: "pointer", color: isCopied ? "#34c38f" : "#74788d", lineHeight: 1 }}
                        >
                            <i className={isCopied ? "bx bx-check fs-5 text-success" : "bx bx-copy fs-6"}></i>
                        </button>
                        {scanUrl && (
                            <a
                                href={scanUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-primary text-decoration-none"
                                title={`View on ${getExplorerName(row.chain_name || row.chain_type, row.withdrawal_address)}`}
                                style={{ lineHeight: 1 }}
                            >
                                <i className="bx bx-link-external fs-6"></i>
                            </a>
                        )}
                    </div>
                );
            }
        },
        {
            header: "Status",
            render: (row) => renderStatusBadge(row.status || row.istatus)
        },
        {
            header: "Action",
            render: (row) => (
                <button
                    type="button"
                    className="btn btn-sm btn-soft-primary d-inline-flex align-items-center gap-1 px-2 py-1"
                    onClick={() => setSelectedWithdraw(row)}
                    title="View Withdrawal Details"
                >
                    <i className="bx bx-show fs-6" />
                    <span>Details</span>
                </button>
            )
        }
    ];

    return (
        <div id="layout-wrapper">
            <Topbar />
            <Sidebar />
            <div className="main-content">
                <div className="page-content">
                    <div className="container-fluid">
                        <div className="row">
                            <div className="col-12">
                                <div className="page-title-box d-flex align-items-center justify-content-between">
                                    <h2 className="fw-700 mb-0">Crypto Withdraw</h2>
                                    <div className="page-title-right">
                                        <button
                                            className="btn btn-primary waves-effect waves-light d-flex align-items-center"
                                            onClick={() => navigate("/withdraw")}
                                        >
                                            <i className="bx bx-wallet me-2 fs-5"></i> Make Withdrawal
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div className="row">
                            <div className="col-12">
                                <div className="card">
                                    <div className="card-body">
                                        <div className="d-flex flex-wrap align-items-end gap-2 mb-4 desktop-filter-row">
                                            <div>
                                                <label htmlFor="fromDate">From Date</label>
                                                <input
                                                    className="form-control"
                                                    type="date"
                                                    name="fromDate"
                                                    value={filters.fromDate}
                                                    onChange={handleChange}
                                                    max={filters.toDate || today}
                                                    id="fromDate"
                                                />
                                            </div>
                                            <div>
                                                <label htmlFor="toDate">To Date</label>
                                                <input
                                                    className="form-control"
                                                    type="date"
                                                    name="toDate"
                                                    min={filters.fromDate || undefined}
                                                    max={today}
                                                    value={filters.toDate}
                                                    onChange={handleChange}
                                                    id="toDate"
                                                />
                                            </div>
                                            <div>
                                                <label htmlFor="search">Search</label>
                                                <input
                                                    className="form-control"
                                                    type="text"
                                                    id="search"
                                                    placeholder="Search..."
                                                    value={searchTerm}
                                                    onChange={(e) => setSearchTerm(e.target.value)}
                                                />
                                            </div>
                                            <div>
                                                <button
                                                    type="button"
                                                    className="btn btn-primary waves-effect waves-light"
                                                    onClick={handleSearch}
                                                >
                                                    Search
                                                </button>
                                            </div>
                                            <div>
                                                <button
                                                    type="button"
                                                    className="btn btn-primary waves-effect waves-light"
                                                    onClick={clearFilters}
                                                    disabled={!filters.fromDate && !filters.toDate && !searchTerm && !appliedFilters.fromDate && !appliedFilters.toDate && !appliedFilters.search}
                                                >
                                                    Clear
                                                </button>
                                            </div>
                                            <div className="ms-auto">
                                                <ExportButton
                                                    disabled={!filteredData || filteredData.length === 0}
                                                    onClick={handleExport}
                                                />
                                            </div>
                                        </div>

                                        <MobileFilter
                                            title="History"
                                            onExport={handleExport}
                                            exportDisabled={!filteredData || filteredData.length === 0}
                                        >
                                            <div>
                                                <label htmlFor="fromDateMobile">From Date</label>
                                                <input
                                                    className="form-control"
                                                    type="date"
                                                    name="fromDate"
                                                    value={filters.fromDate}
                                                    onChange={handleChange}
                                                    max={filters.toDate || today}
                                                    id="fromDateMobile"
                                                />
                                            </div>
                                            <div>
                                                <label htmlFor="toDateMobile">To Date</label>
                                                <input
                                                    className="form-control"
                                                    type="date"
                                                    name="toDate"
                                                    min={filters.fromDate || undefined}
                                                    max={today}
                                                    value={filters.toDate}
                                                    onChange={handleChange}
                                                    id="toDateMobile"
                                                />
                                            </div>
                                            <div>
                                                <label htmlFor="searchMobile">Search</label>
                                                <input
                                                    className="form-control"
                                                    type="text"
                                                    id="searchMobile"
                                                    placeholder="Search..."
                                                    value={searchTerm}
                                                    onChange={(e) => setSearchTerm(e.target.value)}
                                                />
                                            </div>
                                            <div className="d-flex gap-2 mt-2">
                                                <button
                                                    type="button"
                                                    className="btn btn-primary waves-effect waves-light flex-grow-1"
                                                    onClick={handleSearch}
                                                    data-bs-dismiss="offcanvas"
                                                    data-bs-target="#filterOffcanvasMobile"
                                                >
                                                    Search
                                                </button>
                                                <button
                                                    type="button"
                                                    className="btn btn-light waves-effect waves-light border flex-grow-1"
                                                    onClick={clearFilters}
                                                    disabled={!filters.fromDate && !filters.toDate && !searchTerm && !appliedFilters.fromDate && !appliedFilters.toDate && !appliedFilters.search}
                                                    data-bs-dismiss="offcanvas"
                                                    data-bs-target="#filterOffcanvasMobile"
                                                >
                                                    Clear
                                                </button>
                                            </div>
                                        </MobileFilter>

                                        <CommonTable
                                            key={`${appliedFilters.fromDate}_${appliedFilters.toDate}`}
                                            columns={columns}
                                            data={filteredData}
                                            loading={loading}
                                            loadingMessage={
                                                <div>
                                                    <div className="spinner-border text-primary spinner-border-sm me-2" role="status"></div>
                                                    <span>Loading data...</span>
                                                </div>
                                            }
                                            noDataMessage="No data found"
                                            tableClassName="table table-hover align-middle table-nowrap mb-0"
                                        />
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                <Footer />
            </div>

            {/* Crypto Withdrawal Details Modal */}
            {selectedWithdraw && (
                <>
                    <div
                        className="modal-backdrop fade show"
                        style={{ backgroundColor: "rgba(0, 0, 0, 0.55)", zIndex: 1050 }}
                        onClick={() => setSelectedWithdraw(null)}
                    />

                    <div
                        className="modal fade show d-block"
                        tabIndex="-1"
                        role="dialog"
                        aria-modal="true"
                        style={{ zIndex: 1055 }}
                        onClick={(e) => {
                            if (e.target === e.currentTarget) setSelectedWithdraw(null);
                        }}
                    >
                        <div className="modal-dialog modal-dialog-centered modal-lg" style={{ maxWidth: "720px" }}>
                            <div
                                className="modal-content border-0 shadow-lg"
                                style={{ borderRadius: "16px", overflow: "hidden" }}
                            >
                                {/* Header */}
                                <div className="modal-header bg-light py-3 px-4 border-bottom">
                                    <div className="d-flex align-items-center gap-2">
                                        <div
                                            className="d-flex align-items-center justify-content-center bg-danger bg-opacity-10 text-danger rounded-circle"
                                            style={{ width: "38px", height: "38px" }}
                                        >
                                            <i className="bx bx-wallet fs-4" />
                                        </div>
                                        <div>
                                            <h5 className="modal-title fw-bold text-dark mb-0">Crypto Withdrawal Details</h5>
                                            <small className="text-muted">
                                                Withdrawal ID:{" "}
                                                <span className="font-monospace fw-semibold text-dark">
                                                    #{selectedWithdraw.id}
                                                </span>
                                            </small>
                                        </div>
                                    </div>
                                    <button
                                        type="button"
                                        className="btn-close"
                                        onClick={() => setSelectedWithdraw(null)}
                                        aria-label="Close"
                                    />
                                </div>

                                {/* Body */}
                                <div className="modal-body p-4" style={{ maxHeight: "75vh", overflowY: "auto" }}>
                                    {/* Top Banner: Amount, Fee, Payable & Status */}
                                    <div className="p-3 mb-4 rounded-3 bg-light border d-flex flex-wrap align-items-center justify-content-between gap-3">
                                        <div>
                                            <div className="text-muted font-size-12 fw-medium text-uppercase">
                                                Withdrawal Amount
                                            </div>
                                            <div className="d-flex align-items-baseline gap-2 mt-1">
                                                <span className="fs-3 fw-bold text-danger">
                                                    ${selectedWithdraw.amount != null
                                                        ? Number(selectedWithdraw.amount).toLocaleString(undefined, {
                                                            minimumFractionDigits: 2,
                                                            maximumFractionDigits: 6,
                                                        })
                                                        : "0.00"}
                                                </span>
                                                <span className="badge bg-primary bg-opacity-10 text-primary font-size-13 fw-semibold">
                                                    {selectedWithdraw.token_symbol || selectedWithdraw.coin_symbol || "USDT"}
                                                </span>
                                                <span className="badge bg-secondary bg-opacity-10 text-secondary font-size-12 font-monospace">
                                                    {selectedWithdraw.chain_name || selectedWithdraw.chain_type || "-"}
                                                </span>
                                            </div>
                                        </div>

                                        <div className="text-end">
                                            <div className="text-muted font-size-12 fw-medium text-uppercase mb-1">
                                                Status
                                            </div>
                                            <div>{renderStatusBadge(selectedWithdraw.status || selectedWithdraw.istatus)}</div>
                                            <div className="text-muted font-size-11 mt-1">
                                                <i className="bx bx-calendar me-1" />
                                                {selectedWithdraw.requested_at
                                                    ? formatDateDDMMYYYY(selectedWithdraw.requested_at)
                                                    : formatDateDDMMYYYY(selectedWithdraw.ondate || selectedWithdraw.created_at) || "-"}
                                            </div>
                                        </div>
                                    </div>

                                    {/* Financial Summary Cards */}
                                    <div className="row g-3 mb-4">
                                        <div className="col-4">
                                            <div className="border rounded p-3 text-center bg-white shadow-sm h-100">
                                                <div className="text-muted font-size-12 fw-semibold">Gross Amount</div>
                                                <div className="fw-bold fs-5 text-dark mt-1">
                                                    ${selectedWithdraw.amount != null ? Number(selectedWithdraw.amount).toFixed(2) : "0.00"}
                                                </div>
                                            </div>
                                        </div>
                                        <div className="col-4">
                                            <div className="border rounded p-3 text-center bg-white shadow-sm h-100">
                                                <div className="text-muted font-size-12 fw-semibold">Network Fee</div>
                                                <div className="fw-bold fs-5 text-muted mt-1">
                                                    ${selectedWithdraw.fee_amount != null ? Number(selectedWithdraw.fee_amount).toFixed(2) : "0.00"}
                                                </div>
                                            </div>
                                        </div>
                                        <div className="col-4">
                                            <div className="border rounded p-3 text-center bg-white shadow-sm h-100 border-success">
                                                <div className="text-success font-size-12 fw-semibold">Net Payable</div>
                                                <div className="fw-bold fs-5 text-success mt-1">
                                                    ${selectedWithdraw.payable != null ? Number(selectedWithdraw.payable).toFixed(2) : "0.00"}
                                                </div>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Section 1: Blockchain & Transfer Information */}
                                    <div className="mb-4">
                                        <h6 className="text-uppercase font-size-12 text-primary fw-bold mb-3 d-flex align-items-center gap-1">
                                            <i className="bx bx-transfer-alt fs-5" />
                                            Blockchain Information
                                        </h6>

                                        {/* Withdrawal Address */}
                                        <div className="mb-3">
                                            <div className="d-flex align-items-center justify-content-between mb-1">
                                                <label className="form-label text-muted font-size-12 mb-0 fw-semibold">
                                                    Destination Wallet Address
                                                </label>
                                                {selectedWithdraw.withdrawal_address && (
                                                    <span className="text-muted font-size-11">
                                                        Scanner:{" "}
                                                        <span className="fw-semibold text-dark">
                                                            {getExplorerName(
                                                                selectedWithdraw.chain_name || selectedWithdraw.chain_type,
                                                                selectedWithdraw.withdrawal_address
                                                            )}
                                                        </span>
                                                    </span>
                                                )}
                                            </div>

                                            <div className="p-2 px-3 rounded bg-light border d-flex align-items-center justify-content-between gap-2 flex-wrap">
                                                <span className="font-monospace text-dark text-break font-size-13 fw-medium">
                                                    {selectedWithdraw.withdrawal_address || "-"}
                                                </span>

                                                {selectedWithdraw.withdrawal_address && (
                                                    <div className="d-flex align-items-center gap-2 ms-auto">
                                                        <button
                                                            type="button"
                                                            className="btn btn-sm btn-outline-secondary d-inline-flex align-items-center gap-1 py-1 px-2"
                                                            onClick={() =>
                                                                handleCopyText(
                                                                    selectedWithdraw.withdrawal_address,
                                                                    "modal_with_addr",
                                                                    "Withdrawal Address"
                                                                )
                                                            }
                                                            title="Copy Address"
                                                        >
                                                            <i
                                                                className={
                                                                    copiedKey === "modal_with_addr"
                                                                        ? "bx bx-check text-success"
                                                                        : "bx bx-copy"
                                                                }
                                                            />
                                                            <span>{copiedKey === "modal_with_addr" ? "Copied" : "Copy"}</span>
                                                        </button>

                                                        {getExplorerAddressUrl(
                                                            selectedWithdraw.chain_name || selectedWithdraw.chain_type,
                                                            selectedWithdraw.withdrawal_address
                                                        ) && (
                                                            <a
                                                                href={getExplorerAddressUrl(
                                                                    selectedWithdraw.chain_name || selectedWithdraw.chain_type,
                                                                    selectedWithdraw.withdrawal_address
                                                                )}
                                                                target="_blank"
                                                                rel="noopener noreferrer"
                                                                className="btn btn-sm btn-soft-primary d-inline-flex align-items-center gap-1 py-1 px-2"
                                                                title="Open address on blockchain scanner"
                                                            >
                                                                <i className="bx bx-link-external" />
                                                                <span>View on Scan</span>
                                                            </a>
                                                        )}
                                                    </div>
                                                )}
                                            </div>
                                        </div>

                                        {/* Transaction Hash */}
                                        <div className="mb-3">
                                            <div className="d-flex align-items-center justify-content-between mb-1">
                                                <label className="form-label text-muted font-size-12 mb-0 fw-semibold">
                                                    Transaction Hash (TxID)
                                                </label>
                                            </div>

                                            <div className="p-2 px-3 rounded bg-light border d-flex align-items-center justify-content-between gap-2 flex-wrap">
                                                {selectedWithdraw.tx_hash ? (
                                                    <>
                                                        <span className="font-monospace text-dark text-break font-size-13 fw-medium">
                                                            {selectedWithdraw.tx_hash}
                                                        </span>
                                                        <div className="d-flex align-items-center gap-2 ms-auto">
                                                            <button
                                                                type="button"
                                                                className="btn btn-sm btn-outline-secondary d-inline-flex align-items-center gap-1 py-1 px-2"
                                                                onClick={() =>
                                                                    handleCopyText(
                                                                        selectedWithdraw.tx_hash,
                                                                        "modal_with_tx",
                                                                        "Transaction Hash"
                                                                    )
                                                                }
                                                                title="Copy Transaction Hash"
                                                            >
                                                                <i
                                                                    className={
                                                                        copiedKey === "modal_with_tx"
                                                                            ? "bx bx-check text-success"
                                                                            : "bx bx-copy"
                                                                    }
                                                                />
                                                                <span>{copiedKey === "modal_with_tx" ? "Copied" : "Copy"}</span>
                                                            </button>

                                                            {getExplorerTxUrl(
                                                                selectedWithdraw.chain_name || selectedWithdraw.chain_type,
                                                                selectedWithdraw.tx_hash
                                                            ) && (
                                                                <a
                                                                    href={getExplorerTxUrl(
                                                                        selectedWithdraw.chain_name || selectedWithdraw.chain_type,
                                                                        selectedWithdraw.tx_hash
                                                                    )}
                                                                    target="_blank"
                                                                    rel="noopener noreferrer"
                                                                    className="btn btn-sm btn-soft-primary d-inline-flex align-items-center gap-1 py-1 px-2"
                                                                    title="Open transaction on blockchain scanner"
                                                                >
                                                                    <i className="bx bx-link-external" />
                                                                    <span>View on Scan</span>
                                                                </a>
                                                            )}
                                                        </div>
                                                    </>
                                                ) : (
                                                    <span className="text-muted font-size-13 fst-italic">
                                                        Not available yet (Waiting for admin processing & blockchain broadcast)
                                                    </span>
                                                )}
                                            </div>
                                        </div>
                                    </div>

                                    {/* Section 2: Timing & Processing Information */}
                                    <div className="mb-4">
                                        <h6 className="text-uppercase font-size-12 text-primary fw-bold mb-3 d-flex align-items-center gap-1">
                                            <i className="bx bx-time fs-5" />
                                            Timing & Processing Details
                                        </h6>
                                        <div className="row g-3">
                                            <div className="col-md-6">
                                                <div className="border rounded p-2 px-3 h-100">
                                                    <div className="text-muted font-size-11">Requested Date & Time</div>
                                                    <div className="fw-semibold text-dark mt-1">
                                                        {selectedWithdraw.requested_at
                                                            ? formatDateDDMMYYYY(selectedWithdraw.requested_at)
                                                            : formatDateDDMMYYYY(selectedWithdraw.ondate || selectedWithdraw.created_at) || "-"}
                                                    </div>
                                                </div>
                                            </div>

                                            <div className="col-md-6">
                                                <div className="border rounded p-2 px-3 h-100">
                                                    <div className="text-muted font-size-11">Processed Date & Time</div>
                                                    <div className="fw-semibold text-dark mt-1">
                                                        {selectedWithdraw.processed_at
                                                            ? formatDateDDMMYYYY(selectedWithdraw.processed_at)
                                                            : <span className="text-muted fst-italic">Pending processing</span>}
                                                    </div>
                                                </div>
                                            </div>

                                            <div className="col-md-6">
                                                <div className="border rounded p-2 px-3 h-100">
                                                    <div className="text-muted font-size-11">Network / Chain</div>
                                                    <div className="fw-semibold text-dark mt-1">
                                                        {selectedWithdraw.chain_name || selectedWithdraw.chain_type || "-"}
                                                    </div>
                                                </div>
                                            </div>

                                            <div className="col-md-6">
                                                <div className="border rounded p-2 px-3 h-100">
                                                    <div className="text-muted font-size-11">Reviewed By</div>
                                                    <div className="fw-semibold text-dark mt-1">
                                                        {selectedWithdraw.reviewed_by || <span className="text-muted fst-italic">Under Review</span>}
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Section 3: Account Information */}
                                    {(selectedWithdraw.uloginid ||
                                        selectedWithdraw.uemail ||
                                        selectedWithdraw.email ||
                                        selectedWithdraw.ufname ||
                                        selectedWithdraw.umobile) && (
                                        <div className="mb-3">
                                            <h6 className="text-uppercase font-size-12 text-primary fw-bold mb-3 d-flex align-items-center gap-1">
                                                <i className="bx bx-user-circle fs-5" />
                                                Account Details
                                            </h6>
                                            <div className="row g-3">
                                                {selectedWithdraw.uloginid && (
                                                    <div className="col-sm-6 col-md-3">
                                                        <div className="border rounded p-2 px-3 h-100">
                                                            <div className="text-muted font-size-11">Login ID</div>
                                                            <div className="fw-bold text-dark font-monospace mt-1">
                                                                {selectedWithdraw.uloginid}
                                                            </div>
                                                        </div>
                                                    </div>
                                                )}

                                                {(selectedWithdraw.ufname || selectedWithdraw.ulname) && (
                                                    <div className="col-sm-6 col-md-3">
                                                        <div className="border rounded p-2 px-3 h-100">
                                                            <div className="text-muted font-size-11">Name</div>
                                                            <div className="fw-semibold text-dark mt-1 text-truncate">
                                                                {`${selectedWithdraw.ufname || ""} ${
                                                                    selectedWithdraw.ulname || ""
                                                                }`.trim()}
                                                            </div>
                                                        </div>
                                                    </div>
                                                )}

                                                {(selectedWithdraw.uemail || selectedWithdraw.email) && (
                                                    <div className="col-sm-6 col-md-3">
                                                        <div className="border rounded p-2 px-3 h-100">
                                                            <div className="text-muted font-size-11">Email</div>
                                                            <div
                                                                className="fw-medium text-dark font-size-12 mt-1 text-truncate"
                                                                title={selectedWithdraw.uemail || selectedWithdraw.email}
                                                            >
                                                                {selectedWithdraw.uemail || selectedWithdraw.email}
                                                            </div>
                                                        </div>
                                                    </div>
                                                )}

                                                {selectedWithdraw.umobile && (
                                                    <div className="col-sm-6 col-md-3">
                                                        <div className="border rounded p-2 px-3 h-100">
                                                            <div className="text-muted font-size-11">Mobile</div>
                                                            <div className="fw-medium text-dark font-size-12 mt-1">
                                                                {selectedWithdraw.umobile}
                                                            </div>
                                                        </div>
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    )}

                                    {/* Rejection / Note reason if any */}
                                    {(selectedWithdraw.reject_reason || selectedWithdraw.reason || selectedWithdraw.remarks) && (
                                        <div className="alert alert-danger d-flex align-items-center gap-2 mb-0 mt-3 py-2 px-3">
                                            <i className="bx bx-error-circle fs-4" />
                                            <div>
                                                <strong>Note / Reason:</strong>{" "}
                                                {selectedWithdraw.reject_reason || selectedWithdraw.reason || selectedWithdraw.remarks}
                                            </div>
                                        </div>
                                    )}
                                </div>

                                {/* Footer */}
                                <div className="modal-footer bg-light py-2 px-4 border-top d-flex justify-content-between">
                                    <div className="d-flex align-items-center gap-2">
                                        {getExplorerAddressUrl(
                                            selectedWithdraw.chain_name || selectedWithdraw.chain_type,
                                            selectedWithdraw.withdrawal_address
                                        )}
                                        {getExplorerAddressUrl(
                                            selectedWithdraw.chain_name || selectedWithdraw.chain_type,
                                            selectedWithdraw.withdrawal_address
                                        ) && (
                                            <a
                                                href={getExplorerAddressUrl(
                                                    selectedWithdraw.chain_name || selectedWithdraw.chain_type,
                                                    selectedWithdraw.withdrawal_address
                                                )}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="btn btn-sm btn-outline-primary d-inline-flex align-items-center gap-1"
                                            >
                                                <i className="bx bx-link-external" />
                                                <span>Scan Address</span>
                                            </a>
                                        )}
                                        {selectedWithdraw.tx_hash &&
                                            getExplorerTxUrl(
                                                selectedWithdraw.chain_name || selectedWithdraw.chain_type,
                                                selectedWithdraw.tx_hash
                                            ) && (
                                                <a
                                                    href={getExplorerTxUrl(
                                                        selectedWithdraw.chain_name || selectedWithdraw.chain_type,
                                                        selectedWithdraw.tx_hash
                                                    )}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    className="btn btn-sm btn-outline-primary d-inline-flex align-items-center gap-1"
                                                >
                                                    <i className="bx bx-link-external" />
                                                    <span>Scan TxID</span>
                                                </a>
                                            )}
                                    </div>

                                    <button
                                        type="button"
                                        className="btn btn-secondary px-4"
                                        onClick={() => setSelectedWithdraw(null)}
                                    >
                                        Close
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </>
            )}
        </div>
    );
}

